import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./CategoryUpdate.module.css";
import Toggle from "../../utils/Toggle/Toggle";
import Spinner from "../Spinner/Spinner";
import { API } from "../../config";
import Popup from "../Popup/Popup";
import { createFileFromSource } from "../Catalogs/Product/useAddEditProduct";
import CategoryFields from "./CategoryFields";
import { useImageUpload } from "../ClientLogos/hooks/useImageUpload";
import SubcategoryList from "./SubcategoryList";
import SubcategoryCreateForm from "./SubcategoryCreateForm";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

function CategoryUpdate() {
  const [category, setCategory] = useState();
  const [sort, setSort] = useState();
  const [name, setName] = useState();
  const [nonSelectedImage, setNonSelectedImage] = useState();
  const [selectedImage, setSelectedImage] = useState();
  const [customImage, setCustomImage] = useState();
  const [status, setStatus] = useState(false);
  const [description, setDescription] = useState();
  const [subDescription, setSubDescription] = useState();
  const [show, setShow] = useState(false);
  const [spinn, setSpinn] = useState(false);
  const [spin, setSpin] = useState(true);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [newSubcategory, setNewSubcategory] = useState({
    name: "",
    sortId: "",
    image: "",
    isActive: false,
  });
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subcategoryOrder, setSubcategoryOrder] = useState([]);
  const [isSubcategoryOrderChanged, setIsSubcategoryOrderChanged] =
    useState(false);
  const subcategoryImageInputRef = useRef(null);
  const editSubcategoryImageInputRef = useRef(null);
  const params = useParams();

  const {
    selectedFile: nonSelectedFile,
    imagePreview: nonSelectedPreview,
    handleFileChange: handleNonSelectedFileChange,
    uploadImage: uploadNonSelectedImage,
  } = useImageUpload();

  const {
    selectedFile: selectedFileUpload,
    imagePreview: selectedPreview,
    handleFileChange: handleSelectedFileChange,
    uploadImage: uploadSelectedImage,
  } = useImageUpload();

  const {
    selectedFile: customFile,
    imagePreview: customPreview,
    handleFileChange: handleCustomFileChange,
    uploadImage: uploadCustomImage,
  } = useImageUpload();
  let navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const change = () => {
    setShow(false);
  };

  const handleSubcategoryImageClick = () => {
    subcategoryImageInputRef.current?.click();
  };

  const handleSubcategoryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setNewSubcategory((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleEditSubcategoryImageClick = () => {
    editSubcategoryImageInputRef.current?.click();
  };

  const handleEditSubcategoryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setEditingSubcategory((prev) => ({
          ...prev,
          image: reader.result,
          _localImageFile: file,
        }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const getData = async () => {
    const requestOptions = {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    };
    const response = await fetch(
      `${API}/category/get/${params.id}`,
      requestOptions
    );
    const data = await response.json();
    setCategory(data);
    setSpin(false);
    setSort(data.sortId);
    setName(data.name);
    setNonSelectedImage(data.images[0]);
    setSelectedImage(data.images[1]);
    setDescription(data.description);
    setSubDescription(data.subDescription);
    setStatus(data.custom);
    setCustomImage(data.someField);

    const sortedSubcategories = data.subCategories
      ? [...data.subCategories].sort((a, b) => a.sortId - b.sortId)
      : [];
    setSubcategoryOrder(sortedSubcategories.map((sub) => sub._id));
    setIsSubcategoryOrderChanged(false);

    setNewSubcategory((p) => ({
      ...p,
      sortId: sortedSubcategories.length + 1,
    }));
  };

  const imageUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API}/image-upload`, formData);
    console.log(response);
    return response.data.result;
  };

  const updateSubcategory = async (subcategory) => {
    return await axios.put(
      `${API}/category/${params.id}/sub-category/${subcategory._id}`,
      {
        name: subcategory.name,
        sortId: subcategory.sortId,
        image: subcategory.image,
        isActive: subcategory.isActive,
      }
    );
  };

  const updateSubcategoryOrder = async (order) => {
    return await axios.put(`${API}/category/${params.id}/sub-category/order`, {
      order,
    });
  };

  const handleSubcategoryDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    if (!category || !category.subCategories) return;

    const sortedSubcategories = [...category.subCategories].sort(
      (a, b) => a.sortId - b.sortId
    );

    const oldIndex = sortedSubcategories.findIndex(
      (item) => item._id === active.id
    );
    const newIndex = sortedSubcategories.findIndex(
      (item) => item._id === over.id
    );

    const newArray = arrayMove(sortedSubcategories, oldIndex, newIndex);

    newArray.forEach((item, idx) => {
      item.sortId = idx + 1;
    });

    setCategory((prev) => ({
      ...prev,
      subCategories: newArray,
    }));

    const newOrderIds = newArray.map((sub) => sub._id);
    setIsSubcategoryOrderChanged(
      JSON.stringify(newOrderIds) !== JSON.stringify(subcategoryOrder)
    );
  };

  const handleSaveSubcategoryOrder = async () => {
    if (!category || !category.subCategories) return;

    const sortedSubcategories = [...category.subCategories].sort(
      (a, b) => a.sortId - b.sortId
    );

    const payload = sortedSubcategories.map((sub, idx) => ({
      subCategoryId: sub._id,
      order: idx + 1,
    }));

    try {
      await updateSubcategoryOrder(payload);
      const newOrderIds = sortedSubcategories.map((sub) => sub._id);
      setSubcategoryOrder(newOrderIds);
      setIsSubcategoryOrderChanged(false);
      await getData();
    } catch (error) {
      console.error("Failed to update subcategory order:", error);
    }
  };

  const handleCreateSubcategory = useCallback(
    async (e) => {
      e.preventDefault();
      let imageUrl = newSubcategory.image;
      if (newSubcategory.image) {
        imageUrl = await createFileFromSource(
          newSubcategory.image,
          "subcategory.png"
        );

        const result = await imageUpload(imageUrl);

        imageUrl = result;
        setNewSubcategory((prev) => ({ ...prev, image: result }));
      }
      await axios.post(`${API}/category/${params.id}/sub-category`, {
        name: newSubcategory.name,
        sortId: newSubcategory.sortId,
        image: imageUrl,
        isActive: newSubcategory.isActive,
      });
      setShowSubcategoryForm(false);
      setNewSubcategory({
        name: "",
        sortId: "",
        image: "",
        isActive: false,
      });
      getData();
    },
    [newSubcategory, imageUpload, params.id]
  );

  const handleStartEditSubcategory = (sub) => {
    setEditingSubcategoryId(sub._id);
    setEditingSubcategory({
      _id: sub._id,
      name: sub.name || "",
      sortId: sub.sortId || "",
      image: sub.image || "",
      isActive: sub.isActive ?? true,
    });
  };

  const handleCancelEditSubcategory = () => {
    setEditingSubcategoryId(null);
    setEditingSubcategory(null);
  };

  const handleSaveEditSubcategory = async (e) => {
    e.preventDefault();
    if (!editingSubcategory) return;

    let imageUrl = editingSubcategory.image;
    if (editingSubcategory._localImageFile) {
      imageUrl = await imageUpload(editingSubcategory._localImageFile);
    }

    const payload = {
      ...editingSubcategory,
      image: imageUrl,
    };
    delete payload._localImageFile;

    await updateSubcategory(payload);
    handleCancelEditSubcategory();
    getData();
  };

  useEffect(() => {
    getData();
  }, []);

  const deleteData = () => {
    axios.delete(`${API}/category/${params.id}`);
    navigate("/category");
  };

  const saveData = async (event) => {
    event.preventDefault();
    window.scrollTo(0, 0);
    setSpinn(true);

    try {
      const [uploadedNonSelected, uploadedSelected, uploadedCustom] = await Promise.all([
        nonSelectedFile ? uploadNonSelectedImage(nonSelectedFile) : Promise.resolve(nonSelectedImage),
        selectedFileUpload ? uploadSelectedImage(selectedFileUpload) : Promise.resolve(selectedImage),
        status && customFile ? uploadCustomImage(customFile) : Promise.resolve(customImage),
      ]);

      const data = {
        sortId: sort,
        name: name,
        images: [uploadedNonSelected, uploadedSelected],
        description: description,
        subDescription: subDescription,
        custom: status,
        someField: status ? uploadedCustom : "",
        createdBy: "60e9c7db11ab1428accf8278",
        updatedBy: "60e9c7db11ab1428accf8278",
        deletedBy: "60e9c7db11ab1428accf8278",
      };

      await axios.put(`${API}/category/${params.id}`, data);
      setNonSelectedImage(uploadedNonSelected);
      setSelectedImage(uploadedSelected);
      setShow(true);
      setSpinn(false);
    } catch (err) {
      console.error(err.message);
      setSpinn(false);
    }
  };

  return (
    <>
      {spinn ? <Spinner /> : null}
      {show ? (
        <Popup show={show} switch={change} message="Updated Successfully" />
      ) : null}
      {spin ? (
        <Spinner />
      ) : (
        <div className={styles.categorycontainer}>
          <div className={styles.heading}>
            <h1 className={styles.details}>
              Enter the Category Update/Delete Details
            </h1>
            <form onSubmit={saveData}>
              <div className={styles.items}>
                <CategoryFields
                  sort={sort}
                  setSort={setSort}
                  name={name}
                  setName={setName}
                  nonSelectedImage={nonSelectedImage}
                  nonSelectedImagePreview={nonSelectedPreview}
                  onNonSelectedFileChange={handleNonSelectedFileChange}
                  selectedImage={selectedImage}
                  selectedImagePreview={selectedPreview}
                  onSelectedFileChange={handleSelectedFileChange}
                  description={description}
                  setDescription={setDescription}
                  subDescription={subDescription}
                  setSubDescription={setSubDescription}
                  status={status}
                  setStatus={setStatus}
                  customImage={customImage}
                  customImagePreview={customPreview}
                  onCustomFileChange={handleCustomFileChange}
                />

                <div className={styles.subcategorySection}>
                  {category &&
                    category.subCategories &&
                    category.subCategories.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          justifyContent: "space-between",
                          marginBottom: "16px",
                          alignItems: "center",
                        }}
                      >
                        <h2 className={styles.subcategoryHeading}>
                          Subcategories:
                        </h2>
                        <button
                          type="button"
                          onClick={handleSaveSubcategoryOrder}
                          disabled={!isSubcategoryOrderChanged}
                          className={styles.button}
                          style={{
                            width: "fit-content",
                            opacity: isSubcategoryOrderChanged ? 1 : 0.5,
                            cursor: isSubcategoryOrderChanged
                              ? "pointer"
                              : "not-allowed",
                            fontSize: "11px",
                            alignSelf: "center",
                          }}
                        >
                          Save Order
                        </button>
                      </div>
                    )}

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleSubcategoryDragEnd}
                  >
                    <SortableContext
                      items={
                        category?.subCategories
                          ? category.subCategories
                              .sort((a, b) => a.sortId - b.sortId)
                              .map((sub) => sub._id)
                          : []
                      }
                      strategy={verticalListSortingStrategy}
                    >
                      <SubcategoryList
                        category={category}
                        editingSubcategoryId={editingSubcategoryId}
                        editingSubcategory={editingSubcategory}
                        editSubcategoryImageInputRef={
                          editSubcategoryImageInputRef
                        }
                        setEditingSubcategory={setEditingSubcategory}
                        handleStartEditSubcategory={handleStartEditSubcategory}
                        handleEditSubcategoryImageClick={
                          handleEditSubcategoryImageClick
                        }
                        handleEditSubcategoryImageChange={
                          handleEditSubcategoryImageChange
                        }
                        handleSaveEditSubcategory={handleSaveEditSubcategory}
                        handleCancelEditSubcategory={
                          handleCancelEditSubcategory
                        }
                      />
                    </SortableContext>
                  </DndContext>
                </div>

                <SubcategoryCreateForm
                  showSubcategoryForm={showSubcategoryForm}
                  setShowSubcategoryForm={setShowSubcategoryForm}
                  newSubcategory={newSubcategory}
                  setNewSubcategory={setNewSubcategory}
                  subcategoryImageInputRef={subcategoryImageInputRef}
                  handleSubcategoryImageClick={handleSubcategoryImageClick}
                  handleSubcategoryImageChange={handleSubcategoryImageChange}
                  handleCreateSubcategory={handleCreateSubcategory}
                />

                <div className={styles.button}>
                  <input type="submit" className={styles.bttn} value="Update" />
                  <input
                    type="submit"
                    className={styles.bttn}
                    onClick={deleteData}
                    value="Delete"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default CategoryUpdate;
